/*! boroj-tester-upsell.js — cart-page "discover testers" engine.
 * Consumes the cart taste profile (emitted server-side by sections/boroj-tester-upsell.liquid),
 * scores the tester index (assets/boroj-tester-index.json) with the SHARED engine
 * (window.BorojReco), MMR-diversifies to 3 role-labelled picks, fetches the LIVE cheapest
 * available تستر variant per pick, and renders cards + add-to-cart.
 *
 * Scoring (v1): 45% accord cosine + 20% family + 15% gender, renormalized by the engine
 * (lon/sil/season are documented later enhancements). Never recommends a cart identity in any
 * form. Honesty guard: only accords (verified-grade) drive the "why" text.
 *
 * Testable pieces (scoreCandidate/mmr/similarity) are exported for node under module.exports.
 */
(function (root, factory) {
  var API = factory(root.BorojReco);
  root.BorojTesterUpsell = API;
  if (typeof module !== 'undefined' && module.exports) module.exports = API;
  if (typeof document !== 'undefined') API._boot();
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this), function (R) {
  'use strict';
  R = R || (typeof module !== 'undefined' ? require('./boroj-reco-engine.js') : null);

  var CART_W = { accord: 0.45, family: 0.20, gender: 0.15 }; // lon/sil/season deferred → engine renormalizes

  /* ---------- pure scoring (unit-tested) ---------- */
  // profile = { accords:{accordKey:weight}, family, gender }
  function scoreCandidate(cand, profile) {
    var acc = R.accordsOf(cand);
    if (!acc.length) return null;
    var w = profile.accords || {};
    var cartNorm = Math.sqrt(Object.keys(w).reduce(function (s, k) { return s + w[k] * w[k]; }, 0)) || 1;
    var dot = 0, shared = [];
    acc.forEach(function (a) { if (w[a]) { dot += w[a]; shared.push(a); } });
    if (dot <= 0) return null; // no shared accords → not a match
    var accordScore = Math.min(1, dot / (cartNorm * Math.sqrt(acc.length)));
    var familyScore = (profile.family && cand.f === profile.family) ? 1 : 0;
    var g = R.genderOf(cand);
    var genderScore = (!profile.gender || profile.gender === 'any') ? 0.6 : (g === 'unisex' ? 0.8 : (g === profile.gender ? 1 : 0.2));
    var raw = R.blend([
      { w: CART_W.accord, v: accordScore },
      { w: CART_W.family, v: familyScore },
      { w: CART_W.gender, v: genderScore }
    ], []);
    return { c: cand, raw: raw, acc: acc, shared: shared };
  }

  // similarity for diversity: accord Jaccard (0.7) + same-family (0.3)
  function similarity(a, b) {
    var sa = {}; a.acc.forEach(function (x) { sa[x] = 1; });
    var inter = 0; b.acc.forEach(function (x) { if (sa[x]) inter++; });
    var uni = a.acc.length + b.acc.length - inter;
    var jac = uni ? inter / uni : 0;
    var fam = (a.c.f && a.c.f === b.c.f) ? 1 : 0;
    return 0.7 * jac + 0.3 * fam;
  }

  // Maximal-Marginal-Relevance: pick k balancing relevance and novelty
  function mmr(scored, k, lambda) {
    var picks = [], pool = scored.slice();
    while (picks.length < k && pool.length) {
      var best = 0, bestVal = -Infinity;
      for (var i = 0; i < pool.length; i++) {
        var div = 0;
        for (var j = 0; j < picks.length; j++) { var s = similarity(pool[i], picks[j]); if (s > div) div = s; }
        var val = lambda * pool[i].raw - (1 - lambda) * div;
        if (val > bestVal) { bestVal = val; best = i; }
      }
      picks.push(pool.splice(best, 1)[0]);
    }
    return picks;
  }

  // Build the cart taste profile from RAW emitted cart data (so the Liquid section stays simple).
  // raw = { items:[{ accords:[{n,n_en,p}], family, gender, identity, qty }], purchased:[identity] }
  function buildProfile(raw, lang) {
    var weight = {}, coverage = {}, labels = {}, fam = {}, gen = {};
    (raw.items || []).forEach(function (it) {
      var qty = it.qty || 1;
      var localSeen = {};
      (it.accords || []).forEach(function (a) {
        var key = R.low(a.n_en || a.n); if (!key) return;
        var p = (typeof a.p === 'number' && a.p > 0) ? a.p : 100;
        weight[key] = (weight[key] || 0) + (p / 100) * qty;
        labels[key] = (lang === 'ar') ? (a.n || a.n_en) : (a.n_en || a.n);
        if (!localSeen[key]) { localSeen[key] = 1; coverage[key] = (coverage[key] || 0) + 1; }
      });
      if (it.family) fam[R.low(it.family)] = (fam[R.low(it.family)] || 0) + qty;
      if (it.gender) gen[R.low(it.gender)] = (gen[R.low(it.gender)] || 0) + qty;
    });
    var accords = {};
    Object.keys(weight).forEach(function (k) { accords[k] = weight[k] * (1 + 0.5 * ((coverage[k] || 1) - 1)); });
    function mode(m) { var best = null, bv = -1; Object.keys(m).forEach(function (k) { if (m[k] > bv) { bv = m[k]; best = k; } }); return best; }
    var exclude = [];
    (raw.items || []).forEach(function (it) { if (it.identity) exclude.push(R.normIdentity(it.identity)); });
    (raw.purchased || []).forEach(function (id) { if (id) exclude.push(R.normIdentity(id)); });
    return { accords: accords, accordLabels: labels, family: mode(fam), gender: normGender(mode(gen)), exclude: exclude };
  }
  function normGender(g) { g = R.low(g); if (!g) return 'any'; if (g.indexOf('men') > -1 && g.indexOf('women') === -1) return 'men'; if (g.indexOf('women') > -1) return 'women'; if (g.indexOf('unisex') > -1 || g.indexOf('للجنسين') > -1) return 'unisex'; if (g === 'male') return 'men'; if (g === 'female') return 'women'; return g; }

  // full pipeline: score → exclude cart identities → dedupe by base_id → mmr
  function recommend(index, profile, k) {
    var exclude = {}; (profile.exclude || []).forEach(function (id) { exclude[id] = 1; });
    var seen = {}, scored = [];
    for (var i = 0; i < index.length; i++) {
      var c = index[i];
      if (exclude[c.base_id] || seen[c.base_id]) continue;
      var s = scoreCandidate(c, profile);
      if (!s) continue;
      seen[c.base_id] = 1;
      scored.push(s);
    }
    scored.sort(function (a, b) { return b.raw - a.raw; });
    return mmr(scored, k, 0.7);
  }

  /* ---------- DOM / live tester fetch / render ---------- */
  function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
  function money(cents, tpl) { var v = (cents / 100).toLocaleString('en-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); return (tpl || '{{amount}} EGP').replace('{{amount}}', v); }

  function fetchTesters(picks, need, out) {
    out = out || [];
    if (!picks.length || out.length >= need) return Promise.resolve(out);
    var p = picks.shift();
    return fetch(p.c.u + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (prod) {
        var tv = null;
        (prod.variants || []).forEach(function (v) { if (v.available && /تستر/.test(v.title || '')) { if (!tv || v.price < tv.price) tv = v; } });
        if (tv) out.push({ pick: p, product: prod, tester: tv });
      })
      .catch(function () {})
      .then(function () { return fetchTesters(picks, need, out); });
  }

  function whyText(pick, profile, i18n) {
    var labels = profile.accordLabels || {};
    var names = pick.shared.slice(0, 3).map(function (a) { return labels[a] || R.titleCase(a); });
    if (!names.length) return '';
    var sep = i18n.listSep || ', ', and = i18n.and || 'and';
    var joined = names.length > 1 ? (names.slice(0, -1).join(sep) + ' ' + and + ' ' + names[names.length - 1]) : names[0];
    return ((i18n.whyPrefix || 'Matches the') + ' ' + joined + ' ' + (i18n.whySuffix || 'notes in your cart')).replace(/\s+/g, ' ').trim();
  }

  function render(root, cards, profile, i18n) {
    var roles = [i18n.role1 || 'Perfect Match', i18n.role2 || 'Similar Style', i18n.role3 || 'Try Something New'];
    var stars = ['★', '★★', '★★★'];
    var html = '<div class="btu-head"><span class="btu-line"></span><span class="btu-title">' + esc(i18n.title || 'Discover Your Next Favorite') + '</span><span class="btu-line"></span></div>';
    html += '<div class="btu-grid">';
    cards.forEach(function (card, idx) {
      var c = card.pick.c, tv = card.tester;
      var cta = (i18n.cta || 'Try {{size}} · {{price}}').replace('{{size}}', esc(tv.title)).replace('{{price}}', money(tv.price, i18n.moneyTpl));
      html += '<div class="btu-card">'
        + '<a class="btu-img" href="' + esc(c.u) + '">' + (c.img ? '<img src="' + esc(c.img) + '&width=300" width="300" height="300" loading="lazy" alt="' + esc(c.t) + '">' : '') + '</a>'
        + '<div class="btu-body">'
          + '<div class="btu-role"><span class="btu-stars" aria-hidden="true">' + stars[idx] + '</span> ' + esc(roles[idx]) + '</div>'
          + '<a class="btu-name" href="' + esc(c.u) + '">' + esc(c.t) + '</a>'
          + '<p class="btu-why">' + esc(whyText(card.pick, profile, i18n)) + '</p>'
          + '<button type="button" class="btu-add" data-btu-add data-variant-id="' + tv.id + '">' + esc(cta) + '</button>'
        + '</div>'
        + '</div>';
    });
    html += '</div><p class="btu-msg" hidden></p>';
    root.innerHTML = html;

    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-btu-add]'); if (!btn || btn.disabled) return;
      var id = parseInt(btn.dataset.variantId, 10); var orig = btn.textContent;
      btn.disabled = true; btn.textContent = '…';
      fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: id, quantity: 1 }) })
        .then(function (r) { return r.json(); })
        .then(function () {
          btn.textContent = i18n.added || '✓ Added';
          var msg = root.querySelector('.btu-msg'); if (msg) { msg.textContent = i18n.postAdd || 'Great choice!'; msg.hidden = false; }
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          setTimeout(function () { window.location.reload(); }, 1400);
        })
        .catch(function () { btn.textContent = orig; btn.disabled = false; });
    });
  }

  // Process one root (cart page OR a fresh mini-cart drawer render). Idempotent via data-btu-done.
  function processRoot(root) {
    if (!R || !root || root.getAttribute('data-btu-done')) return;
    root.setAttribute('data-btu-done', '1');
    function island(id) { var el = root.querySelector('script[data-' + id + ']'); if (!el) return null; try { return JSON.parse(el.textContent || 'null'); } catch (e) { return null; } }
    var raw = island('btu-cart');
    if (!raw || !raw.items || !raw.items.length) return;
    var profile = buildProfile(raw, root.getAttribute('data-lang') || 'ar');
    if (!profile.accords || !Object.keys(profile.accords).length) return; // no fragrance accords → stay hidden
    var i18n = island('btu-i18n') || {};
    var count = parseInt(root.getAttribute('data-count'), 10) || 3;
    fetch(root.getAttribute('data-index'), { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (index) {
        var picks = recommend(index, profile, count + 5); // buffer for stock/tester filtering
        if (!picks.length) return;
        return fetchTesters(picks.slice(), count).then(function (cards) { if (cards.length) render(root, cards, profile, i18n); });
      })
      .catch(function () {});
  }
  // Scan the whole document for unprocessed roots (cart page + drawer). Re-run when the drawer re-renders.
  function processAll() {
    if (!R || typeof document === 'undefined') return;
    var roots = document.querySelectorAll('[data-boroj-tester-upsell]:not([data-btu-done])');
    for (var i = 0; i < roots.length; i++) processRoot(roots[i]);
  }
  function ready(fn) { if (typeof document === 'undefined') return; if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn); else fn(); }

  return {
    scoreCandidate: scoreCandidate, similarity: similarity, mmr: mmr, recommend: recommend, buildProfile: buildProfile,
    _boot: function () {
      ready(processAll);
      if (typeof document !== 'undefined') {
        // Monkeys Marketing Theme re-renders sections/mini_cart.liquid + fires cart:updated on every cart change → re-scan for the fresh drawer root.
        document.addEventListener('cart:updated', function () { setTimeout(processAll, 80); });
      }
    }
  };
});
